import { useEffect, useState } from "react"
import { AlertCircle, ArrowLeft, RefreshCw } from "lucide-react"

import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ConfettiBurst } from "@/components/confetti-burst"
import { DebriefReport, DebriefSkeleton } from "@/components/debrief-report"
import { PrintButton } from "@/components/print-button"
import { ReportFooter, ReportMasthead } from "@/components/report-masthead"
import { RunCoachChat } from "@/components/run-coach-chat"
import { SelectionQuoteArea } from "@/components/selection-quote-area"
import { usePersonas, useProcessRun } from "@/lib/queries"
import { useDebriefStream } from "@/lib/use-debrief-stream"
import { coachPossessive, useCoachById } from "@/lib/use-coach"

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"

function DebriefPage() {
  const { runId } = Route.useParams()
  const navigate = useNavigate()
  // Only for replaying the persisted run-coach thread; the report itself
  // comes from the debrief stream.
  const { data: run } = useProcessRun(runId)
  const { data: personas = [] } = usePersonas()
  const persona = personas.find((p) => p.id === run?.personaId)
  const coach = useCoachById(run?.coachStyleId)
  const coachName = coachPossessive(coach)

  // A report excerpt the advisor selected to reference in the coach chat.
  const [quote, setQuote] = useState<string | null>(null)

  // Confetti exactly once, right after finishing the last phase: the run page
  // marks the fresh completion before redirecting here. Reopening an old
  // debrief stays quiet.
  const [celebrate, setCelebrate] = useState(false)
  useEffect(() => {
    const key = `debrief-feier-${runId}`
    if (sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key)
      setCelebrate(true)
    }
  }, [runId])

  /**
   * Ein Durchlauf über eine einzige Phase hat hier nichts zu suchen: das
   * Debrief würde die Reflexion dieser Phase wiederholen statt etwas
   * zusammenzufassen. Wer den Link noch gespeichert hat, landet auf dem
   * Durchlauf, und der Modellaufruf unten passiert nicht.
   */
  const einzelphase = run ? run.phases.length === 1 : undefined
  useEffect(() => {
    if (!einzelphase) return
    void navigate({ to: "/durchlauf/$runId", params: { runId }, replace: true })
  }, [einzelphase, navigate, runId])

  const { debrief, done, failed, slow, retry } = useDebriefStream(
    runId,
    einzelphase === false
  )

  const confetti = celebrate && (
    <ConfettiBurst onDone={() => setCelebrate(false)} />
  )

  // Same shape as the feedback stream's slow hint: a retry bar below the
  // (partial or skeleton) content once the stream takes unusually long.
  const slowHint = slow && !done && (
    <div className="flex items-center justify-between gap-3 rounded-md border border-[var(--rule)] bg-card px-4 py-3">
      <span className="text-[13.5px] text-muted-foreground">
        Die Auswertung dauert ungewöhnlich lange.
      </span>
      <Button variant="outline" size="sm" onClick={retry}>
        <RefreshCw className="h-3.5 w-3.5" />
        Erneut versuchen
      </Button>
    </div>
  )

  if (failed && !done) {
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-lg items-center px-6">
        <Card className="w-full items-center border-[var(--rule)] p-8 text-center">
          <AlertCircle className="h-6 w-6 text-primary" />
          <div>
            <h1 className="font-display text-[20px] font-medium">
              Debrief konnte nicht geladen werden
            </h1>
            <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
              Bitte versuche es erneut. Falls das Gesprächstraining noch nicht
              abgeschlossen ist, kannst du zunächst zur Übersicht zurückkehren.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button type="button" onClick={retry}>
              <RefreshCw className="h-4 w-4" />
              Erneut versuchen
            </Button>
            <Button asChild variant="outline">
              <Link to="/durchlauf">
                <ArrowLeft className="h-4 w-4" />
                Zur Übersicht
              </Link>
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  if (!debrief) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4">
        {confetti}
        <DebriefSkeleton />
        {slowHint}
      </div>
    )
  }

  return (
    <>
      {/* Print-only, kept out of the space-y stack so it does not push a top
          margin onto the first on-screen row via the sibling selector. */}
      {done && (
        <ReportMasthead
          title="Gesamtauswertung über alle Einheiten"
          coachName={coachName}
          personaName={persona?.name}
          date={run?.startedAt}
        />
      )}
      {/* Eine Lesespalte, keine Bildschirmbreite: der Debrief ist ein Bericht,
        und über die volle Breite der App reißen die Zeilen auseinander. */}
      <div className="mx-auto w-full max-w-3xl space-y-6">
        {confetti}
        {done && (
          <div className="no-print flex flex-wrap justify-end gap-2">
            <PrintButton />
          </div>
        )}
        {/* Selection unlocks with the chat: while the report still streams,
          re-renders would invalidate the selection ranges anyway. */}
        <SelectionQuoteArea
          enabled={done}
          onQuote={setQuote}
          className="space-y-6"
        >
          <DebriefReport
            debrief={debrief}
            runId={runId}
            coachName={coachName}
            coachLabel={coach?.name}
            coachPortraitUrl={coach?.portraitUrl}
            nachbereitung={run?.caseState.nachbereitung?.antworten}
          />
        </SelectionQuoteArea>

        {slowHint}

        {/* Run-wide coach: grounded in every phase's transcript + feedback.
          Natural height — the thread grows with the conversation and scrolls
          with the page. */}
        <section className="no-print space-y-1">
          <h2 className="font-display text-[18px] font-medium tracking-tight">
            Mit dem Coach besprechen
          </h2>
          {/* Mount only once the run is loaded: useChat seeds its thread on
            mount, so a late-arriving coachChat would otherwise be dropped. */}
          {run && (
            <RunCoachChat
              runId={runId}
              coachStyleId={run.coachStyleId}
              ready={done}
              // Gated on done so a string truncated mid-stream never becomes a
              // pill; old stored narratives lack the field and fall back to the
              // generic set inside RunCoachChat.
              suggestedQuestions={
                done
                  ? debrief?.suggestedQuestions?.filter(
                      (q): q is string => typeof q === "string"
                    )
                  : undefined
              }
              initialMessages={run.coachChat}
              quote={quote}
              onQuoteClear={() => setQuote(null)}
            />
          )}
        </section>

        <div className="no-print">
          <Button asChild variant="outline">
            <Link to="/durchlauf">
              <ArrowLeft className="h-4 w-4" />
              Zur Übersicht der Gesprächstrainings
            </Link>
          </Button>
        </div>

        {done && <ReportFooter />}
      </div>
    </>
  )
}

export const Route = createFileRoute("/_app/durchlauf/$runId_/debrief")({
  component: DebriefPage,
})
